import { I18nProvider } from "@lingui/react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { activateLocale, DEFAULT_LOCALE, detectLocale, type SupportedLocale } from "./index";
import { i18n } from "./init"; // Use pre-initialized i18n

interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: SupportedLocale;
  configLocale?: string;
}

export function LocaleProvider({ children, initialLocale, configLocale }: LocaleProviderProps) {
  // Detect and activate locale synchronously on initial render
  const detectedLocale = useMemo(() => {
    const locale = initialLocale || detectLocale(configLocale);
    // Only activate if different from current locale
    if (i18n.locale !== locale) {
      i18n.activate(locale);
      localStorage.setItem("widget-locale", locale);
    }
    return locale;
  }, [initialLocale, configLocale]);

  const [locale, setLocale] = useState<SupportedLocale>(detectedLocale);
  const [isLoading, setIsLoading] = useState(false); // No loading since i18n is pre-initialized
  const [error, setError] = useState<string | null>(null);

  // Update locale if config changes after initial render
  useEffect(() => {
    const newLocale = initialLocale || detectLocale(configLocale);
    if (newLocale !== locale) {
      try {
        i18n.activate(newLocale);
        setLocale(newLocale);
        localStorage.setItem("widget-locale", newLocale);
        setError(null);
      } catch (err) {
        console.error("Failed to activate locale, falling back to English:", err);
        i18n.activate(DEFAULT_LOCALE);
        setLocale(DEFAULT_LOCALE);
      }
    }
  }, [initialLocale, configLocale, locale]);

  // Expose locale switching function globally for debugging
  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Global debug utility needs any type
    (window as any).changeLocale = async (newLocale: SupportedLocale) => {
      setIsLoading(true);
      await activateLocale(newLocale);
      setLocale(newLocale);
      setIsLoading(false);
      // Reload the page to apply new translations
      window.location.reload();
    };
  }, []);

  if (isLoading) {
    // You can customize this loading state
    return null;
  }

  if (error) {
    // Show error message but still render children with fallback locale
    console.error("Translation loading error:", error);
  }

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
}
